import { FocusEvent, forwardRef, useId, useMemo, useState } from "react";
import { InputRef } from "../input/types";
import { Search } from "./components/search";
import { Options } from "./components/options";
import { Container } from "./components/container";
import { NOT_FOUND_LABEL_TEXT } from "./constants";
import { OptionType, InputAutocompleteProps } from "./types";

export const InputAutocomplete = forwardRef<InputRef, InputAutocompleteProps>(
  (
    {
      id: idFromProps,
      hint,
      error,
      label,
      className,
      disabled = false,
      dataTestId = "input-autocomplete",
      notFoundLabel = NOT_FOUND_LABEL_TEXT,
      isLoading = false,
      clearable = true,
      options,
      value,
      onBlur,
      onFocus,
      onClear,
      onSearch,
      onSelect,
    },
    forwardedRef
  ) => {
    const generatedId = useId();
    const id = idFromProps || generatedId;

    const [expanded, setExpanded] = useState(false);

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      setExpanded(false);
      onBlur?.(event);
    };

    const handleOptionClick = (option: OptionType) => {
      onSelect(option);
      setExpanded(false);
    };

    const handleInputChange = (searchValue: string) => {
      const correctSearchValueLength = searchValue.trim().length > 2;

      onSearch(searchValue);

      setExpanded(correctSearchValueLength);
    };

    const shortenedOptions = useMemo(() => options?.slice(0, 5), [options]);

    const search = (
      <Search
        id={id}
        hint={hint}
        error={error}
        label={label}
        value={value}
        ref={forwardedRef}
        disabled={disabled}
        clearable={clearable}
        options={shortenedOptions}
        dataTestId={`${dataTestId}-input`}
        onFocus={onFocus}
        onClear={onClear}
        onChange={handleInputChange}
      />
    );

    return (
      <Container
        disabled={disabled}
        expanded={expanded}
        className={className}
        dataTestId={dataTestId}
        searchComponent={search}
        onBlur={handleBlur}
      >
        <Options
          id={`${id}-autocomplete-popup`}
          isLoading={isLoading}
          options={shortenedOptions}
          notFoundLabel={notFoundLabel}
          searchValue={value.toLowerCase()}
          dataTestId={`${dataTestId}-options`}
          onClick={handleOptionClick}
        />
      </Container>
    );
  }
);

InputAutocomplete.displayName = "InputAutocomplete";
